import HttpFactory from "~/repository/factory";
import type { LeadState, RegisteredData, OneHourTalkData, Profession, Kinship, Country, ChangeAquarioResponse, AquarioContextoResponse } from "~/types/lead";
import type { LeadBeneficiary, CreateLeadBeneficiaryPayload } from "~/types/proposal";
import { ODataBuilder, odata } from "~/utils/odata";
import type { CompleteMultipartUpload } from "~/types/storage/complete-multipart-upload";

class LeadModule extends HttpFactory {
  private RESOURCE = "/lead";

  /**
   * Busca Leads usando OData Builder
   * Permite passar uma função de callback para construir a query
   */
  async getLeads(
    queryCallback?: (builder: ODataBuilder) => ODataBuilder,
  ): Promise<LeadState[]> {
    let builder = odata();

    if (queryCallback) {
      builder = queryCallback(builder);
    }

    return await this.get<LeadState[]>(this.RESOURCE, {
      query: builder.build(),
    });
  }

  /**
   * Busca Leads usando OData Builder
   * Permite passar uma função de callback para construir a query
   */
  async getFullLead(
    queryCallback?: (builder: ODataBuilder) => ODataBuilder,
  ): Promise<LeadState> {
    let builder = odata();

    if (queryCallback) {
      builder = queryCallback(builder);
    }

    return await this.get<LeadState>(`${this.RESOURCE}/full-state`, {
      query: builder.build(),
    });
  }

  async getLeadDetails(leadId: string): Promise<LeadState> {
    return await this.get<LeadState>(`${this.RESOURCE}/${leadId}/details`);
  }

  async getCepQualification(leadId: string): Promise<any> {
    return await this.get<any>(`${this.RESOURCE}/${leadId}/cep-qualification`);
  }

  async getNearbyLeads(leadId: string): Promise<any> {
    return await this.get<any>(`${this.RESOURCE}/${leadId}/nearby-leads`);
  }

  async getLeadsByUser(
    arquivado: boolean = false,
    hasProposta: boolean = false,
  ): Promise<LeadState[]> {
    return await this.get<LeadState[]>(`${this.RESOURCE}/by-user`, {
      query: {
        arquivado: String(arquivado),
        hasProposta: String(hasProposta),
      },
    });
  }

  async getAllLeads(params: {
    arquivado?: boolean
    aquario_id?: string
    vendedor_id?: string
    created_from?: string
    created_to?: string
  } = {}): Promise<LeadState[]> {
    const query: Record<string, string> = { arquivado: String(params.arquivado ?? false) }
    if (params.aquario_id) query.aquario_id = params.aquario_id
    if (params.vendedor_id) query.vendedor_id = params.vendedor_id
    if (params.created_from) query.created_from = params.created_from
    if (params.created_to) query.created_to = params.created_to
    return await this.get<LeadState[]>(`${this.RESOURCE}/all`, { query })
  }

  async reassignLead(leadId: string, newUserId: string): Promise<{ success: boolean }> {
    return await this.put<{ success: boolean }>(`${this.RESOURCE}/${leadId}/reassign`, { newUserId });
  }

  async deleteLead(leadId: string): Promise<{ success: boolean }> {
    return await this.delete<{ success: boolean }>(`${this.RESOURCE}/${leadId}`);
  }

  async toggleArquivado(leadId: string): Promise<{ arquivado: boolean }> {
    return await this.put<{ arquivado: boolean }>(`${this.RESOURCE}/${leadId}/arquivar`);
  }

  async createLead(data: Partial<RegisteredData>): Promise<LeadState> {
    return await this.post<LeadState>(`${this.RESOURCE}`, data);
  }

  async changeAquario(
    leadId: string,
    payload: { aquario_id_novo: string; codigo_digitado: string; motivo?: string },
  ): Promise<ChangeAquarioResponse> {
    return this.post<ChangeAquarioResponse>(`${this.RESOURCE}/${leadId}/aquario`, payload);
  }

  /**
   * Troca de aquário como administrador — rota admin (POST /lead/admin/:id/aquario).
   * Diferente da rota de vendedor, NÃO bloqueia a variante `propostaSubmetida`:
   * a proposta já submetida permanece intocada no aquário anterior (não retroage).
   */
  async adminChangeAquario(
    leadId: string,
    payload: { aquario_id_novo: string; codigo_digitado: string; motivo?: string },
  ): Promise<ChangeAquarioResponse> {
    return this.post<ChangeAquarioResponse>(`${this.RESOURCE}/admin/${leadId}/aquario`, payload);
  }

  async getAquarioContexto(leadId: string): Promise<AquarioContextoResponse> {
    return this.get<AquarioContextoResponse>(`${this.RESOURCE}/${leadId}/aquario-contexto`);
  }

  async updateLead(id: string, data: RegisteredData): Promise<LeadState> {
    return await this.put<LeadState>(`${this.RESOURCE}/${id}`, data);
  }

  async startOHTUpload(
    id: string,
    mimeType?: string,
  ): Promise<{ uploadId: string; key: string }> {
    return await this.post(
      `${this.RESOURCE}/${id}/one-hour-talk/upload/start`,
      mimeType ? { mimeType } : undefined,
    );
  }

  async getOHTPartUrl(payload: {
    key: string;
    uploadId: string;
    partNumber: number;
  }): Promise<{ url: string }> {
    return await this.post(
      `${this.RESOURCE}/one-hour-talk/upload/presigned-url`,
      payload,
    );
  }

  async completeOHTUpload(
    payload: CompleteMultipartUpload,
  ): Promise<{ success: boolean; url: string }> {
    return await this.post(
      `${this.RESOURCE}/one-hour-talk/upload/complete`,
      payload,
    );
  }

  async getOHTTranscript(payload: {
    key: string;
  }): Promise<{ transcript: string | null; summary?: unknown }> {
    return await this.post(
      `${this.RESOURCE}/one-hour-talk/transcript`,
      payload,
    );
  }

  async saveOneHourTalkData(
    leadId: string,
    data: OneHourTalkData,
  ): Promise<{ success: boolean; message: string; leadId: string }> {
    return await this.post(`${this.RESOURCE}/${leadId}/one-hour-talk`, data);
  }

  async requalifyLead(leadId: string): Promise<LeadState> {
    return await this.post<LeadState>(`${this.RESOURCE}/${leadId}/requalify`);
  }

  async requalifyCep(leadId: string): Promise<{ success: boolean; message: string }> {
    return await this.post<{ success: boolean; message: string }>(`${this.RESOURCE}/${leadId}/requalify-cep`);
  }

  async markAsOptOut(
    leadId: string,
  ): Promise<{ success: boolean; message: string }> {
    return await this.post(`${this.RESOURCE}/${leadId}/opt-out`);
  }

  async getPressignedAudioUrl(payload: { key: string }): Promise<string> {
    return await this.post(
      `${this.RESOURCE}/one-hour-talk/presigned-url`,
      payload,
    );
  }

  async listProfessions(): Promise<Profession[]> {
    return await this.get<Profession[]>(`${this.RESOURCE}/professions`);
  }

  async listKinships(): Promise<Kinship[]> {
    return await this.get<Kinship[]>(`${this.RESOURCE}/kinships`);
  }

  async listCountries(): Promise<Country[]> {
    return await this.get<Country[]>(`${this.RESOURCE}/countries`);
  }

  async getLeadBeneficiaries(leadId: string): Promise<LeadBeneficiary[]> {
    return await this.get<LeadBeneficiary[]>(`${this.RESOURCE}/${leadId}/beneficiaries`);
  }

  async addLeadBeneficiary(
    leadId: string,
    data: CreateLeadBeneficiaryPayload,
  ): Promise<LeadBeneficiary> {
    return await this.post<LeadBeneficiary>(`${this.RESOURCE}/${leadId}/beneficiaries`, data);
  }

  async updateLeadBeneficiary(
    leadId: string,
    beneficiaryId: string,
    data: Partial<CreateLeadBeneficiaryPayload>,
  ): Promise<LeadBeneficiary> {
    return await this.put<LeadBeneficiary>(
      `${this.RESOURCE}/${leadId}/beneficiaries/${beneficiaryId}`,
      data,
    );
  }

  async deleteLeadBeneficiary(
    leadId: string,
    beneficiaryId: string,
  ): Promise<{ success: boolean }> {
    return await this.delete<{ success: boolean }>(
      `${this.RESOURCE}/${leadId}/beneficiaries/${beneficiaryId}`,
    );
  }

  async getMeetingRecordings(conversationId: string): Promise<{
    recordings: Array<{
      presignedUrl: string
      displayName: string
      offsetSec: number
      durationSec: number | null
      startedAt: string | null
      endedAt: string | null
    }>;
  }> {
    return await this.get(`/meeting/conversation/${conversationId}/recordings`);
  }
}

export default LeadModule;
